import React from 'react';
import {Box, Button, Stack} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import {ChildConditionGroup, ConditionNode} from '../../headerAnalysisRuleModel';
import {ConditionRow} from './ConditionRow';
import {OperatorSelector} from './OperatorSelector';

interface ConditionGroupProps {
  group: ChildConditionGroup;
  conditionErrors: Record<string, string>;
  isDuplicate: boolean;
  rootPosition: number;
  simpleNumbering: boolean;
  onOperator: (groupId: string, operator: 'all' | 'any') => void;
  onAdd: (groupId: string) => void;
  onDetail: (condition: ConditionNode) => void;
  onRemove: (conditionId: string) => void;
}

export const ConditionGroup: React.FC<ConditionGroupProps> = ({
  group, conditionErrors, isDuplicate, rootPosition, simpleNumbering,
onOperator, onAdd, onDetail, onRemove,
}) => (
  <Box
    className={`trigger-condition-group${isDuplicate ? ' trigger-condition-group--duplicate' : ''}`}
    data-testid="condition-group-2"
    data-duplicate={isDuplicate}
    aria-invalid={isDuplicate}
    sx={{
      position: 'relative',
      pl: isDuplicate ? 2 : 1.5,
      pr: isDuplicate ? 2 : 0,
      pt: isDuplicate ? 1 : 0,
      pb: 1,
      bgcolor: isDuplicate ? '#fae3e4' : 'transparent',
      borderRadius: '8px',
      boxSizing: 'border-box',
      width: '100%',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: '8px',
        boxSizing: 'border-box',
        borderLeft: group.operator === 'any' ? '2px dashed #ff8d35' : '1px solid #3a618e',
        borderRadius: '8px 0 0 8px',
        pointerEvents: 'none',
      },
    }}
  >
    {group.children.length > 1 && (
      <OperatorSelector
        groupId={group.id}
        operator={group.operator}
        accessibleLabel={`Group ${rootPosition} operator`}
        onChange={onOperator}
      />
    )}
    <Stack spacing={1.5}>
      {group.children.map((condition, conditionIndex) => (
        <ConditionRow
          key={condition.id}
          condition={condition}
          error={conditionErrors[condition.id]}
          number={simpleNumbering ? `${conditionIndex + 1}.` : `${rootPosition}.${conditionIndex + 1}.`}
          onDetail={onDetail}
          onRemove={onRemove}
        />
      ))}
    </Stack>
    <Button
      startIcon={<AddIcon/>}
      onClick={() => onAdd(group.id)}
      aria-label={`Add condition to group ${rootPosition}`}
      sx={{mt: isDuplicate ? 1.5 : 1}}
    >
      Add Condition
    </Button>
  </Box>
);
